import { useContext } from 'react'

import { useAtomValue } from 'jotai'
import { isNil } from 'ramda'

import type {
  LinksDropdownLinkComponent,
  LinksHeaderDropdownComponent,
  LinksLinkComponent,
  LinksSimpleLinkComponent,
  Navigation,
} from '@twisto/api-cms'
import { useIsMobile } from '@twisto/hooks'

import { BaseLink } from 'homepage/components/atoms'
import { DropdownLink, HeaderDropdown } from 'homepage/components/molecules'

import { navigationThemeAtom } from '../../navigation-theme'
import { NestedLinksContext } from '../../nested-links-provider'

type Props = {
  primaryNavigationContent: Navigation['primary']
  increaseFontSize?: boolean
}

type LinkComponent =
  | (LinksLinkComponent & { __component: 'links.link' })
  | (LinksSimpleLinkComponent & { __component: 'links.simple-link' })
  | (LinksDropdownLinkComponent & { __component: 'links.dropdown-link' })
  | (LinksHeaderDropdownComponent & { __component: 'links.header-dropdown' })

export const PrimaryComponents = ({ primaryNavigationContent }: Props) => {
  const { currentActiveListId } = useContext(NestedLinksContext) ?? {}
  const theme = useAtomValue(navigationThemeAtom)
  const isMobile = useIsMobile()

  if (isNil(primaryNavigationContent)) {
    return null
  }

  const componentsArray = primaryNavigationContent.map((component, index) => {
    const linkComponent = component as LinkComponent

    switch (linkComponent.__component) {
      case 'links.link':
        return (
          <BaseLink
            key={index}
            hide={!!currentActiveListId}
            inverted={theme === 'dark' && !isMobile}
            {...linkComponent}
          />
        )
      case 'links.simple-link':
        return (
          <BaseLink
            key={index}
            fontWeight="medium"
            hide={!!currentActiveListId}
            hover="primary"
            inverted={theme === 'dark' && !isMobile}
            variant="primary"
            {...(linkComponent as unknown as LinksLinkComponent)}
          />
        )
      case 'links.dropdown-link':
        return <DropdownLink key={index} {...linkComponent} />
      case 'links.header-dropdown':
        return <HeaderDropdown key={index} {...linkComponent} />
      default:
        return null
    }
  })

  return <>{componentsArray}</>
}
